import split from 'lodash/split';
import axios from 'axios';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import * as types from '../constants/ActionTypes';
import Page from '../../lib/page';
import {
  isFinalIntentEnabledForSetContext,
  isSetContextEnabledFromRC,
} from '../../lib/omnichannelHelper';
import { getIcfMappings } from '../../home/actions/action-icf-guide';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import { getNNAIcfId } from '../../lib/nnaHelper';
import getHistory from '../../lib/HistoryHelper';
import {
  isOmniChannelAPITriggeredInSession,
  removeOmniChannelAPITriggeredInSession,
  omniChannelAPITriggeredInSession,
} from '../../lib/globalHelper';
import UrlResources from '../../home/constants/UrlResources';

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

const ARTICLE_URL = '/smarthelp/article-content';

export function requestArticle() {
  return {
    type: types.REQUEST_ARTICLE,
    payload: undefined,
  };
}

export function receiveArticle(responseData) {
  return {
    type: types.RECEIVE_ARTICLE_RESPONSE,
    payload: responseData,
  };
}

export function receiveArticleError() {
  return {
    type: types.RECEIVE_ARTICLE_ERROR,
    payload: undefined,
  };
}

export function resetOmniChannelPromptInfo() {
  return {
    type: types.RESET_OMNI_CHANNEL_PROMPT_INFO,
  };
}

/**
 * Set context POST call with available tags information
 */
export function setContextApiCall(intents, faq, customerConfirmation = false) {
  const deviceType = Page.getDeviceType();
  // Returning customer conformed true only in contact us page or
  // if the article is clicked which has intents in search page
  const customerConfirmed = customerConfirmation || Page.isContactUsPage();

  const setContextOptions = {
    _csrf: document.body.getAttribute('data-token'),
    intents,
    deviceType,
    faq,
  };

  // only if customer is confirmed wee will be sending the customer confirmed key
  if (customerConfirmed) {
    setContextOptions.customerConfirmed = customerConfirmed;
  }

  // FPTI For Set context
  trackLink({
    id: 'setContext',
    link: 'smarthelp-set-context',
    ocp_context_metadata: JSON.stringify(intents),
    context_status: 'ACTIVE',
    link_name: customerConfirmed,
    channel_name: 'HELP_CENTER',
  });
  return axios.post('/smarthelp/set-context', setContextOptions).then(() => {
    // Fpti when set context is success
    trackPage({ id: 'setContextSuccess', link: 'smarthelp-set-context-success', hpid: faq });
  }).catch(() => {
    // Fpti when set context is failed
    trackPage({ id: 'setContextFailure', link: 'smarthelp-set-context-failure', hpid: faq });
  });
}

/**
 * checking If elmo is enabled, user loggedin and article response has tags
 */
export function isSetContextEnabled(responseBody, topicrefId, faq) {
  // Checking for final intent existance in remote config
  const isIntentsEnabled = isFinalIntentEnabledForSetContext(topicrefId, responseBody);
  // Checking for tags information in articles
  if (Page.isLoggedIn() // Logged-in customer
    && isSetContextEnabledFromRC() // enabled in RC
    && Array.isArray(isIntentsEnabled) // Has intents available
    && isIntentsEnabled.length > 0) { // At least one intent available
    return setContextApiCall(isIntentsEnabled, faq);
  }
  return null;
}

export function fetchArticle(faqId, checkTechnical = false) {
  return (dispatch, getState) => {
    dispatch(requestArticle());
    const faq = split(faqId, ',')[0];

    let articleURL = `${ARTICLE_URL}?article_id=${faq}`;

    const topicRefId = _get(getState(), 'selectResult.topicTreeSelectResult.topicRefId', null);

    // Checking the session storage for omni channel API triggered value from session
    const setContextTriggered = isOmniChannelAPITriggeredInSession();

    if (checkTechnical) {
      articleURL = Page.setChannelForTechnicalExp(articleURL);
    }

    return axios.get(articleURL, {
      cancelToken: source.token,
    })
      .then((response) => {
        if (response.data && response.data.articleContent) {
          dispatch(receiveArticle(response.data.articleContent));

          // If set context API already triggered when clicked on search results do not trigger the API again
          if (!setContextTriggered) {
            isSetContextEnabled(response.data, topicRefId, faq);
          }

          // Removing OmniChannel API triggered value from session
          removeOmniChannelAPITriggeredInSession();
        }
      })
      .catch(() => {
        dispatch(receiveArticleError());

        // If set context API already triggered when clicked on search results do not trigger the API again
        if (!setContextTriggered) {
          isSetContextEnabled({}, topicRefId, faq);
        }

        // Removing OmniChannel API triggered value from session
        removeOmniChannelAPITriggeredInSession();
      });
  };
}

export function cancelArtilceRequest() {
  source.cancel('article request canceled.');
}

/**
 * Update context POST call with available intents information
 */
export function updateContext(articleContent, icfId, customerConfirmed) {
  const articleDetails = (articleContent && articleContent.contextMetaData) || '';
  const deviceType = Page.getDeviceType();
  const contextId = (articleDetails && articleDetails.context_id) || '';

  // if icf id exists pass icf id else pass the article id
  const faq = icfId || articleContent.articleId;

  // pass intent of article
  const intents = articleDetails && articleDetails.intent;

  // Only if we have intents, contextId and faq call update context service
  if (!_isEmpty(intents) && faq && contextId) {
    // setting the session variable when omni channel API is triggered, only when customerConfirmed is true
    // customerConfirmed will be false only when NO is clicked on the omni channel prompt
    if (customerConfirmed) {
      omniChannelAPITriggeredInSession(true);
    }

    const timeStamp = Math.round(+new Date() / 1000);

    // FPTI For update context
    trackLink({
      id: 'updateContext',
      link: 'smarthelp-update-context',
      ocp_context_metadata: JSON.stringify(intents),
      timestamp: timeStamp,
      context_status: 'ACTIVE',
      link_name: customerConfirmed,
      channel_name: 'HELP_CENTER',
    });

    return axios.post('/smarthelp/update-context', {
      _csrf: document.body.getAttribute('data-token'),
      intents,
      contextId,
      deviceType,
      faq,
      customerConfirmed,
    }).then(() => {
      // Fpti when update context is success
      trackPage({ id: 'updateContextSuccess', link: 'smarthelp-update-context-success', hpid: faq });
    }).catch(() => {
      // Fpti when update context is failed
      trackPage({ id: 'updateContextFailure', link: 'smarthelp-update-context-failure', hpid: faq });
    });
  }

  // if the required information is not  available for firing update context call, thne firing an FPTI call
  return trackPage({ id: 'omniChannelUpdateContextNotFired', link: 'smarthelp-update-context-not-fired', hpid: faq });
}

/**
 * Function to return BNA-ID if ICF-ID is passed
 * @param icfId
 * @returns {Array}
 */
function returnBnaId(icfId) {
  const bnaMapping = getIcfMappings();
  const bnaId = [];
  Object.keys(bnaMapping).forEach((key) => {
    if (bnaMapping[key] === icfId) {
      // Pushing all the BNA's which match ICF articles
      bnaId.push(key);
    }
  });
  return bnaId;
}

/**
 * Function to get BNA article mapped to ICF and call update con text API
 */
export function icfArticleUpdateContext(icfArticle, articles, isNNAIcf = false) {
  // This is the common update context for regular ICF's and NNA ICF. If update context is initiated from NNA article
  // get icf id from remote config. if not get the icfId from icf article.
  // By default isNNAIcf will false

  let icfId = null;
  if (isNNAIcf) {
    icfId = getNNAIcfId();
  } else {
    icfId = icfArticle && icfArticle.icf_id;
  }

  if (icfId) {
    // get bnaid with icfid
    const BnaId = returnBnaId(icfId);
    /* eslint-disable array-callback-return */
    return articles.filter((article) => { // eslint-disable-line consistent-return
      // checking for BNA ID in the list of BNA id returned from icf remote config mapping
      if (article.channel === 'BNA' && BnaId.indexOf(article.channelDetails.bnaId) >= 0) {
        return updateContext(article, icfId, true);
      }
    });
  }
  return trackPage({ id: 'updateContextFailure', link: 'smarthelp-update-context-failure-null-icf-id' });
}

/**
 * Function triggered when clicked on yes on omnichannel prompt in home page
 */
export function updateContextPromptConfirmation(articleInfo) {
  return (dispatch) => {
    const faqId = articleInfo.articleId || '';
    // FPTI
    trackLink({ id: 'omniChannelPromptYes', link: `smarthelp-omnichannel-prompt-yes|suggested-solution-${faqId}` });

    // Set/update context
    updateContext(articleInfo, '', true);
    dispatch(resetOmniChannelPromptInfo());

    // If FaqId exists redirect to article page
    if (faqId) {
      return getHistory().push(`/${UrlResources.ARTICLE}/${faqId}`);
    }
    // if faq doesnt exists closing the omnichannel prompt and fpti is triggered
    return trackPage({ id: 'omniChannelPromptYesFaqNotExist', link: 'smarthelp-omnichannel-prompt-yes-faq-not-available' });
  };
}
/**
 * Function triggered when clicked on yes on omnichannel prompt in home page
 */
export function updateContextPromptRejection(articleInfo) {
  return (dispatch) => {
    const faqId = articleInfo.articleId || '';
    trackLink({ id: 'omniChannelPromptNo', link: `smarthelp-omnichannel-prompt-clicked-no|suggested-solution-${faqId}` });
    dispatch(resetOmniChannelPromptInfo());
    return updateContext(articleInfo, '', false);
  };
}
